import { Card } from './ui';
import MeterBar from './charts/MeterBar.jsx';
import { SERIES } from './charts/chartColors.js';

// T141 / Analytics-Roadmap P1 — the "Today" daily-cadence card, shared between
// the Analytics page and the Dashboard "Today" zone. Presentational: takes a
// cadence object + an onSetGoal(next) callback. Theme-safe utilities only.

const STEP_BTN =
  'flex h-5 w-5 items-center justify-center rounded border border-slate-700 text-slate-300 transition hover:border-brand-600 hover:text-brand-400 disabled:opacity-40';

export function GoalStepper({ goal, onSetGoal }) {
  const v = goal ?? 0;
  return (
    <div className="flex items-center gap-1.5 text-xs text-slate-400">
      <span>Daily goal</span>
      <button type="button" aria-label="Decrease daily goal" disabled={v <= 0} onClick={() => onSetGoal(v - 1)} className={STEP_BTN}>
        −
      </button>
      <span className="w-5 text-center font-mono text-sm text-slate-200">{goal ?? '—'}</span>
      <button type="button" aria-label="Increase daily goal" disabled={v >= 50} onClick={() => onSetGoal(v + 1)} className={STEP_BTN}>
        +
      </button>
    </div>
  );
}

export default function TodayCard({ cadence, onSetGoal }) {
  const { goal, appliedToday, appliedThisWeek, byDay, daysHitGoalThisWeek, goalMetToday } = cadence;
  const pct = goal ? Math.min(100, Math.round((appliedToday / goal) * 100)) : 0;
  const remaining = goal ? Math.max(0, goal - appliedToday) : 0;
  const maxBar = Math.max(1, goal || 0, ...byDay.map((d) => d.count));
  return (
    <Card padding="md">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-[12rem]">
          <p className="text-xs uppercase tracking-wide text-slate-500">Applied today</p>
          <p className="mt-0.5 flex items-baseline gap-1.5">
            <span className={'text-3xl font-semibold ' + (goalMetToday ? 'text-emerald-300' : 'text-slate-100')}>{appliedToday}</span>
            {goal != null && <span className="text-sm text-slate-500">/ {goal} goal</span>}
          </p>
          {goal != null ? (
            <>
              <div className="mt-2 max-w-[16rem]">
                <MeterBar frac={pct / 100} color={goalMetToday ? SERIES.up : SERIES.accent} />
              </div>
              <p className="mt-1 text-xs text-slate-500">{goalMetToday ? '✓ goal met today' : `${remaining} more to hit your goal`}</p>
            </>
          ) : (
            <p className="mt-1 text-xs text-slate-500">Set a daily goal to track your pace.</p>
          )}
          <div className="mt-2">
            <GoalStepper goal={goal} onSetGoal={onSetGoal} />
          </div>
        </div>

        <div className="sm:text-right">
          <p className="text-xs uppercase tracking-wide text-slate-500">Last 7 days</p>
          <div className="mt-1.5 flex h-10 items-end gap-1">
            {byDay.map((d, i) => {
              const h = Math.max(8, Math.round((d.count / maxBar) * 40));
              const isToday = i === byDay.length - 1;
              const hit = goal && d.count >= goal;
              return (
                <div key={d.date} className="flex w-5 flex-col items-center justify-end" title={`${d.date}: ${d.count}`}>
                  <div
                    className={'w-full rounded-sm ' + (isToday ? 'bg-brand-500' : hit ? 'bg-emerald-500' : 'bg-slate-700')}
                    style={{ height: `${h}px` }}
                  />
                </div>
              );
            })}
          </div>
          <p className="mt-1 text-xs text-slate-500">
            <span className="text-slate-300">{appliedThisWeek}</span> this week
            {goal != null && (
              <>
                {' '}· <span className="text-slate-300">{daysHitGoalThisWeek}</span>/7 days hit goal
              </>
            )}
          </p>
        </div>
      </div>
    </Card>
  );
}
